import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { BarChart3, Sliders, Users, UserCircle, LogIn, LogOut, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';
import ApprovedBadge from '@/components/ApprovedBadge';

const ROLE_BADGE = {
  admin:     'bg-red-500',
  manager:   'bg-orange-500',
  team:      'bg-blue-500',
  installer: 'bg-green-500',
};

const versionStamp = () => {
  const iso = process.env.REACT_APP_BUILD_TIME;
  if (!iso) return 'Version checked on —';
  const d = new Date(iso);
  const date = d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
  return `Version checked on ${date} at ${time}`;
};

/**
 * Shared portal header for Home, Staff Dashboard and other top-level pages.
 * One compact bar on every screen size: title + who you are on the left, icon nav on the right
 * (labels appear from the sm breakpoint). activeSection — 'home' | 'staff' | undefined.
 */
export default function ProPortalHeader({ activeSection }) {
  const navigate = useNavigate();
  const { user, isAuthenticated, isStaff, loading, logout } = useAuth();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const navBtnClass = (key, base) => `${base} h-10 w-10 sm:h-9 sm:w-auto px-0 sm:px-3 ${activeSection === key ? 'bg-opacity-10 ring-2 ring-offset-1 font-semibold' : ''}`;
  const NavBtn = ({ testId, onClick, cls, Icon, label, activeKey }) => (
    <Button data-testid={testId} onClick={onClick} variant="outline" size="sm" aria-label={label} title={label} className={navBtnClass(activeKey, cls)}>
      <Icon className="w-4 h-4 sm:mr-1.5" />
      <span className="hidden sm:inline">{label}</span>
    </Button>
  );

  return (
    <header className="bg-white shadow-sm border-b-4 border-[#27292E]">
      <div className="max-w-6xl mx-auto px-3 sm:px-4 py-2.5 sm:py-3 flex items-center gap-2 sm:gap-4">
        <div className="min-w-0 flex-1">
          <h1 className="text-base sm:text-xl font-bold text-[#27292E] leading-tight truncate"><span className="sm:hidden">Ox ProPortal</span><span className="hidden sm:inline">Ox Resin ProPortal</span></h1>
          {!loading && isAuthenticated ? (
            <div className="mt-0.5 flex items-center gap-1.5 text-[11px] sm:text-xs text-gray-600 min-w-0" data-testid="header-user-identity">
              <UserCircle className="w-3.5 h-3.5 text-gray-400 shrink-0" />
              <span className="font-medium truncate">{user?.name}</span>
              <ApprovedBadge user={user} compact />
              <span className={`text-[10px] text-white px-1.5 py-px rounded-full capitalize shrink-0 ${ROLE_BADGE[user?.role] || 'bg-gray-500'}`}>{user?.role}</span>
              {!isStaff && user?.account_code && (
                <span className="shrink-0" data-testid="account-code-display">
                  · Code <span className="font-mono font-bold text-[#006400]">{user.account_code}</span>
                </span>
              )}
              <span className="hidden lg:inline text-gray-400 truncate" data-testid="version-stamp">· {versionStamp()}</span>
            </div>
          ) : (
            <p className="text-[11px] text-gray-400" data-testid="version-stamp">{versionStamp()}</p>
          )}
        </div>

        {!loading && isAuthenticated && (
          <nav className="flex items-center gap-1.5 sm:gap-2 shrink-0" aria-label="Portal navigation">
            <NavBtn testId="header-analytics-btn" onClick={() => navigate('/analytics')} activeKey="analytics" Icon={BarChart3} label="Analytics" cls="border-emerald-600 text-emerald-700 hover:bg-emerald-50" />
            <NavBtn testId="header-settings-btn" onClick={() => navigate('/settings')} activeKey="settings" Icon={Sliders} label="Business Profile" cls="border-slate-500 text-slate-700 hover:bg-slate-100" />
            {isStaff && (
              <>
                <NavBtn testId="header-staff-btn" onClick={() => navigate('/staff')} activeKey="staff" Icon={Users} label="Staff Dashboard" cls="border-[#7A0B7A] text-[#7A0B7A] hover:bg-[#7A0B7A]/10" />
                <NavBtn testId="header-installer-view-btn" onClick={() => navigate('/')} activeKey="home" Icon={Home} label="Installer View" cls="border-[#006400] text-[#006400] hover:bg-[#006400]/10" />
              </>
            )}
            <NavBtn testId="header-logout-btn" onClick={handleLogout} Icon={LogOut} label="Logout" cls="border-gray-300 text-gray-600 hover:bg-gray-100" />
          </nav>
        )}

        {!loading && !isAuthenticated && (
          <Button onClick={() => navigate('/login')} className="bg-[#27292E] hover:bg-[#3a3d42] text-white shrink-0">
            <LogIn className="w-4 h-4 mr-2" />
            Login
          </Button>
        )}
      </div>
    </header>
  );
}
